// Wishlist (Tarea 19, Req. 14.1, 14.4–14.7, 16.1, 16.2): vista de lista completa
// ordenada alfabéticamente por artista+título, con filtros (género, artista,
// título, prioridad "super necesario") y orden alternativo; alta/edición/baja de
// ítems y marca/desmarca de prioridad.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api/ApiContext.js';
import { friendlyError } from '../api/friendlyError.js';
import type { WishlistQuery } from '../api/endpoints.js';
import { useAuth } from '../auth/AuthContext.js';
import { useAsync } from '../ui/useAsync.js';
import { useToast } from '../ui/ToastProvider.js';
import { ConfirmDialog } from '../ui/Modal.js';
import {
  AlertMessage,
  AsyncBoundary,
  ButtonSpinner,
  Card,
  EmptyState,
  Icon,
  PageHeader,
  SkeletonList,
} from '../ui/components.js';
import type { WishlistItem } from '../types.js';

export function WishlistView() {
  const { t } = useTranslation();
  const api = useApi();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('wishlist', 'read_write');

  const [artist, setArtist] = useState('');
  const [title, setTitle] = useState('');
  const [onlySuper, setOnlySuper] = useState(false);
  const [sort, setSort] = useState('');
  const [dir, setDir] = useState<'asc' | 'desc'>('asc');
  const [applied, setApplied] = useState<WishlistQuery>({});
  const [adding, setAdding] = useState(false);
  // Ítem pendiente de confirmación de borrado (reemplaza el confirm() nativo).
  const [toRemove, setToRemove] = useState<WishlistItem | null>(null);
  const [removing, setRemoving] = useState(false);

  const list = useAsync(() => api.wishlist.list(applied), [JSON.stringify(applied)]);

  // ¿Hay filtros activos? Para distinguir "wishlist vacía" (coaching) de
  // "sin coincidencias con el filtro".
  const hasActiveFilters = useMemo(
    () => Object.values(applied).some((v) => v !== undefined && v !== null && v !== ''),
    [applied],
  );

  const query = useMemo<WishlistQuery>(
    () => ({
      artist: artist || undefined,
      title: title || undefined,
      super_necesario: onlySuper || undefined,
      sort: sort || undefined,
      dir,
    }),
    [artist, title, onlySuper, sort, dir],
  );

  async function toggleSuper(item: WishlistItem) {
    try {
      await api.wishlist.setSuper(item.id, !item.super_necesario);
      list.reload();
    } catch (err) {
      toast.error(friendlyError(err, t));
    }
  }

  async function confirmRemove() {
    if (!toRemove) return;
    setRemoving(true);
    try {
      await api.wishlist.remove(toRemove.id);
      toast.success(t('wishlist.removed'));
      list.reload();
    } catch (err) {
      toast.error(friendlyError(err, t));
    } finally {
      setRemoving(false);
      setToRemove(null);
    }
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        eyebrow={t('wishlist.eyebrow')}
        title={t('wishlist.title')}
        subtitle={t('wishlist.subtitle')}
        actions={
          canEdit ? (
            <button
              className="btn-secondary btn-sm"
              type="button"
              onClick={() => setAdding((a) => !a)}
            >
              <Icon name={adding ? 'close' : 'add'} className="text-[16px]" />{' '}
              {adding ? t('wishlist.close') : t('wishlist.add')}
            </button>
          ) : undefined
        }
      />

      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        {adding && canEdit && (
          <AddWishlistForm
            onCreated={() => {
              setAdding(false);
              toast.success(t('wishlist.added'));
              list.reload();
            }}
          />
        )}

        <div data-tour="wishlist-filters">
          <Card title={t('wishlist.filters')} icon="filter_list">
            <div className="grid grid-cols-2 gap-space-sm">
              <Field label={t('wishlist.artist')}>
                <input
                  className="field-input"
                  value={artist}
                  onChange={(e) => setArtist(e.target.value)}
                />
              </Field>
              <Field label={t('wishlist.title2')}>
                <input
                  className="field-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Field>
              <Field label={t('wishlist.order')}>
                <select
                  className="field-input"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                >
                  <option value="">{t('wishlist.orderArtistTitle')}</option>
                  <option value="artist">{t('wishlist.artist')}</option>
                  <option value="title">{t('wishlist.title2')}</option>
                  <option value="genre">{t('wishlist.orderGenre')}</option>
                  <option value="super_necesario">{t('wishlist.orderPriority')}</option>
                  <option value="created_at">{t('wishlist.orderAdded')}</option>
                </select>
              </Field>
              <Field label={t('wishlist.direction')}>
                <select
                  className="field-input"
                  value={dir}
                  onChange={(e) => setDir(e.target.value as 'asc' | 'desc')}
                >
                  <option value="asc">{t('wishlist.asc')}</option>
                  <option value="desc">{t('wishlist.desc')}</option>
                </select>
              </Field>
            </div>
            <label
              data-tour="wishlist-only-super"
              className="flex items-center gap-space-xs mt-space-sm font-body-sm text-body-sm text-on-surface cursor-pointer"
            >
              <input
                type="checkbox"
                className="accent-[#38bdf8] w-4 h-4"
                checked={onlySuper}
                onChange={(e) => setOnlySuper(e.target.checked)}
              />
              {t('wishlist.onlySuper')}
            </label>
            <div className="flex items-center gap-space-sm mt-space-sm">
              <button
                className="btn-primary flex-1"
                type="button"
                onClick={() => setApplied(query)}
              >
                {t('wishlist.apply')}
              </button>
              <button
                className="btn-ghost"
                type="button"
                onClick={() => {
                  setArtist('');
                  setTitle('');
                  setOnlySuper(false);
                  setSort('');
                  setApplied({});
                }}
              >
                {t('wishlist.clear')}
              </button>
            </div>
          </Card>
        </div>

        <AsyncBoundary state={list} fallback={<SkeletonList count={4} />}>
          {(d) =>
            d.items.length === 0 ? (
              hasActiveFilters ? (
                <EmptyState icon="search_off" message={t('wishlist.noMatches')} />
              ) : (
                // Wishlist vacía de primer uso: guía a buscar y guardar un disco.
                <EmptyState
                  icon="bookmark"
                  title={t('wishlist.emptyTitle')}
                  message={t('wishlist.emptyBody')}
                  action={
                    <Link to="/quick-lookup" className="btn-cyan">
                      <Icon name="search" className="text-[18px]" /> {t('wishlist.emptyCta')}
                    </Link>
                  }
                />
              )
            ) : (
              <Card title={t('wishlist.items', { count: d.total })} icon="favorite">
              <ul className="flex flex-col gap-space-sm">
                {d.items.map((item) => (
                  <li
                    key={item.id}
                    className={`surface-rack-card !p-space-sm flex items-start justify-between gap-space-sm ${
                      item.super_necesario ? 'border-priority-urgent/50' : ''
                    }`}
                  >
                    <div className="min-w-0 flex flex-col gap-0.5">
                      {/* Renglón 1: Artista */}
                      <span className="font-body-md text-body-md font-bold text-on-surface truncate">
                        {item.artist_name}
                      </span>
                      {/* Renglón 2: Título */}
                      <span className="font-body-md text-body-md text-on-surface-variant truncate">
                        {item.title === '*' ? t('wishlist.anyRecord') : item.title}
                      </span>
                      {/* Género · año (si hay) */}
                      {(item.genre_label || item.release_year) && (
                        <span className="font-body-sm text-body-sm text-on-surface-variant truncate">
                          {item.genre_label ?? ''}{' '}
                          {item.release_year ? `· ${item.release_year}` : ''}
                        </span>
                      )}
                      {/* Último renglón: indicación de súper necesario (sin interferir con el texto) */}
                      {item.super_necesario && (
                        <span className="mt-space-xs self-start inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-priority-urgent/15 border border-priority-urgent/50 text-priority-urgent font-badge-micro text-badge-micro uppercase">
                          <span className="w-1.5 h-1.5 rounded-full bg-priority-urgent animate-pulse" />{' '}
                          {t('wishlist.superNeeded')}
                        </span>
                      )}
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-space-xs shrink-0">
                        <button
                          className={`w-9 h-9 rounded flex items-center justify-center ${
                            item.super_necesario
                              ? 'bg-priority-urgent/20 text-priority-urgent'
                              : 'bg-surface-container text-on-surface-variant'
                          }`}
                          type="button"
                          aria-label={
                            item.super_necesario
                              ? t('wishlist.removePriority')
                              : t('wishlist.prioritize')
                          }
                          onClick={() => void toggleSuper(item)}
                        >
                          <Icon name="priority_high" className="text-[18px]" />
                        </button>
                        <button
                          className="w-9 h-9 rounded bg-surface-container text-priority-urgent flex items-center justify-center"
                          type="button"
                          aria-label={t('wishlist.remove')}
                          onClick={() => setToRemove(item)}
                        >
                          <Icon name="delete" className="text-[18px]" />
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              </Card>
            )
          }
        </AsyncBoundary>
      </div>

      {/* Confirmación de borrado (reemplaza el confirm() nativo). */}
      <ConfirmDialog
        open={toRemove !== null}
        onConfirm={() => void confirmRemove()}
        onCancel={() => setToRemove(null)}
        title={t('wishlist.remove')}
        message={
          toRemove
            ? t('wishlist.removeConfirm', {
                name: `${toRemove.artist_name} - ${toRemove.title === '*' ? t('wishlist.anyRecord') : toRemove.title}`,
              })
            : ''
        }
        confirmLabel={t('wishlist.remove')}
        tone="danger"
        busy={removing}
        icon="delete"
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-space-xs">
      <label className="field-label">{label}</label>
      <div className="field-slot">{children}</div>
    </div>
  );
}

function AddWishlistForm({ onCreated }: { onCreated: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const [artist, setArtist] = useState('');
  const [title, setTitle] = useState('*');
  const [superN, setSuperN] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.wishlist.create({
        artist_name: artist,
        title: title || '*',
        super_necesario: superN,
      });
      onCreated();
    } catch (err) {
      setError(friendlyError(err, t, 'wishlist.addError'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t('wishlist.addTitle')} icon="add_circle">
      <AlertMessage message={error} className="mb-space-sm" />
      <form onSubmit={submit} className="flex flex-col gap-space-sm">
        <div className="grid grid-cols-2 gap-space-sm">
          <Field label={t('wishlist.artist')}>
            <input
              className="field-input"
              value={artist}
              onChange={(e) => setArtist(e.target.value)}
              required
            />
          </Field>
          <Field label={t('wishlist.titleAnyHint')}>
            <input
              className="field-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>
        </div>
        <label className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface cursor-pointer">
          <input
            type="checkbox"
            className="accent-[#f43f5e] w-4 h-4"
            checked={superN}
            onChange={(e) => setSuperN(e.target.checked)}
          />
          {t('wishlist.superNeeded')}
        </label>
        <button
          className="btn-primary w-full inline-flex items-center justify-center gap-space-xs"
          type="submit"
          disabled={busy}
        >
          {busy && <ButtonSpinner />}
          {busy ? t('wishlist.saving') : t('wishlist.save')}
        </button>
      </form>
    </Card>
  );
}
